/**
 * The toolbar preset: a long bar with everything on it, or a short one.
 *
 * <h2>What the mode is</h2>
 * A view preference stored per browser, exactly like the brand override beside it. It hides two
 * runs of toolbar icons and changes the product's name — three things that move together, so they
 * are one setting rather than three.
 */

/** The key, as specified. */
export const BAR_MODE_KEY = 'toolbar_bar_mode';

/** Fired when the mode changes in THIS tab — `storage` only reaches other tabs. */
export const BAR_MODE_CHANGED_EVENT = 'astrocharts:bar-mode-changed';

export const LONG = 'long';
export const SHORT = 'short';

/** The default. A first visit gets the full toolbar. */
export const DEFAULT_BAR_MODE = LONG;

/** The name the product takes in the short preset — header and tab title. */
export const SHORT_BAR_BRAND = 'Dhanabhyas';

/**
 * What the chart watermark says in the short preset.
 *
 * <p>⚠ THE SAME STRING AS {@link SHORT_BAR_BRAND} NOW, where it used to be the domain form. The
 * argument for a domain is unchanged and still recorded in `utils/branding.js`: a watermark
 * travels, and an address identifies the product to someone who does not already have it open. It
 * loses here only because the deployment does not serve a matching domain, and a watermark
 * pointing at an address that does not resolve is worse provenance than a bare name.
 *
 * <p>Kept as its OWN constant rather than aliased to the brand. The two answer different questions
 * — what the header calls the product, and what an exported image says it came from — and they
 * have already differed once; collapsing them would make differing again a refactor.
 */
export const SHORT_BAR_WATERMARK = 'Dhanabhyas';

/** How many icons are dropped from the front of the bar. */
export const SHORT_BAR_HIDE_LEADING = 8;

/**
 * Where the second run starts, found by its ALT TEXT rather than by an index.
 *
 * <p>The ICONS array is assembled with spreads — the three CTS buttons and the two cycle buttons
 * are generated — so a source position is not a runtime position, and the two drift apart every
 * time a generated group gains or loses a member. An index written down today would hide the wrong
 * ten icons the next time one is added, silently and in the middle of the bar.
 */
export const SHORT_BAR_FLASK_ALT = 'Strategy 1';

/** How many icons are dropped from the flask onwards, the flask included. */
export const SHORT_BAR_HIDE_FROM_FLASK = 10;

function storage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (_) {
    return null;
  }
}

/** The stored mode, or the default. Anything unrecognised reads as the default. */
export function readBarMode() {
  try {
    const raw = storage()?.getItem(BAR_MODE_KEY);
    return raw === SHORT ? SHORT : DEFAULT_BAR_MODE;
  } catch (_) {
    return DEFAULT_BAR_MODE;
  }
}

/** Stores a mode and announces it. Returns the mode now in force. */
export function writeBarMode(mode) {
  const next = mode === SHORT ? SHORT : LONG;
  try {
    storage()?.setItem(BAR_MODE_KEY, next);
  } catch (_) {
    // Quota or a blocked store: the mode still applies for this session through the event below.
  }
  try {
    window.dispatchEvent(new CustomEvent(BAR_MODE_CHANGED_EVENT, { detail: next }));
  } catch (_) { /* no window: the caller still has the return value */ }
  return next;
}

/** The other mode. */
export function otherMode(mode) {
  return mode === SHORT ? LONG : SHORT;
}

/**
 * What the toggle button should say.
 *
 * <p>The label is the ACTION, not the state: in the long preset it offers "Short Bar". A button
 * labelled with the state it is already in reads as a status line and gets clicked by someone
 * trying to confirm it.
 */
export function barModeButtonLabel(mode) {
  return mode === SHORT ? 'Long Bar' : 'Short Bar';
}

/** Subscribes to changes from this tab or another one. Returns an unsubscribe. */
export function onBarModeChange(onChange) {
  const local = e => onChange(e && 'detail' in e ? e.detail : readBarMode());
  const remote = (e) => {
    if (!e || e.key === null || e.key === BAR_MODE_KEY) onChange(readBarMode());
  };
  window.addEventListener(BAR_MODE_CHANGED_EVENT, local);
  window.addEventListener('storage', remote);
  return () => {
    window.removeEventListener(BAR_MODE_CHANGED_EVENT, local);
    window.removeEventListener('storage', remote);
  };
}

/**
 * The toolbar positions hidden in a given mode.
 *
 * <p>A Set of INDEXES into the array it was given, so the renderer skips positions rather than
 * building a second array — which keeps every existing `key={idx}` and every index-based lookup in
 * that render loop pointing at the same entry it always did.
 *
 * @param {Array<object>} icons the runtime ICONS array
 * @param {string} mode
 * @returns {Set<number>}
 */
export function hiddenIconIndices(icons, mode) {
  const hidden = new Set();
  if (mode !== SHORT || !Array.isArray(icons)) return hidden;

  const leading = Math.min(SHORT_BAR_HIDE_LEADING, icons.length);
  for (let i = 0; i < leading; i++) hidden.add(i);

  const flask = icons.findIndex(icon => icon && icon.alt === SHORT_BAR_FLASK_ALT);
  if (flask >= 0) {
    const end = Math.min(flask + SHORT_BAR_HIDE_FROM_FLASK, icons.length);
    for (let i = flask; i < end; i++) hidden.add(i);
  }
  return hidden;
}
